import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RestService } from '../../../services/rest/rest.service';
import { RestParams } from '../../../services/rest/rest-request';
import { DateDecode } from '../../../services/rest/rest-json-codec.service';
import { DetailedQuote } from '../../../domain/quote.domain';
import {
  QuoteIssueType,
  QuoteTimePeriod,
  QuoteTimeRange,
  QuoteTimeSeries
} from './domain';

export interface QuoteParams extends RestParams {
  market: string;
  symbol: string;
  cusip?: string;
  accountId?: string;
  includeDrip?: boolean;
}

export interface QuoteTimeRangeParams extends QuoteParams {
  timeRange: QuoteTimeRange;
}

export interface QuoteFrequencyParams extends QuoteTimeRangeParams {
  timePeriod: QuoteTimePeriod;
  interval?: number;
  frequency?: QuoteTimePeriod;
}

/**
 * Markets Quotes Rest Service
 *
 * Get markets quotes data
 *
 */

@Injectable({
  providedIn: 'root'
})
export class QuoteRestService {
  private readonly restService = inject(RestService);

  private readonly quotesResourceId: string = 'markets.quote';
  private readonly timeSeriesResourceId: string = 'markets.timeSeries';
  private readonly warningCategory: string = 'marketQuotes';
  private readonly pennyStockValue: number = 5.00;

  /**
   * Get quote
   *
   * @param params QuotesParams object containing market and symbol
   * @returns
   */
  getQuote(params: QuoteParams): Observable<DetailedQuote> {
    const quoteObservable =  this.getQuotesRest(this.quotesResourceId, params);
    return quoteObservable
      .pipe(
        map((quote: DetailedQuote): DetailedQuote => {
          if (quote === null) { quote = undefined; }
          if (quote !== undefined && quote.issueType === QuoteIssueType.STOCK) {
            quote.isPennyStock = this.isPennyStock(quote);
          }
          return quote;
        })
      );
  }

  /**
   * Get time series
   *
   * @param params QuotesTimeRangeParams object market, symbol and timeRange
   * @returns
   */
  getTimeSeries(params: QuoteTimeRangeParams): Observable<QuoteTimeSeries> {
    return this.getQuotesRest(this.timeSeriesResourceId, params);
  }

  /**
   * Get time series and enable frequency
   *
   * @param: market, symbol, timeRange, timeperiod and interval
   * @returns
   */
  getTimeSeriesEnableFrequency(params: QuoteFrequencyParams): Observable<QuoteTimeSeries> {
    const restParams: QuoteFrequencyParams = {
      market: params.market,
      symbol: params.symbol,
      timeRange: params.timeRange,
      timePeriod: params.timePeriod
    };
    if (params.interval !== undefined) {
      restParams.interval = params.interval;
    } else {
      // TODO: could not find call which pass in frequency
      // Remove once confirmed
      restParams.timePeriod = params.frequency;
    }
    return this.getQuotesRest(this.timeSeriesResourceId, restParams);
  }

  /**
   * Get Quotes Rest
   *
   * @param
   * @returns
   */
  private getQuotesRest(resourceId: string, params: QuoteParams): Observable<DetailedQuote | QuoteTimeSeries> {
    return this.restService.getResource(resourceId).get({
      params,
      responseDateDecode: DateDecode.MILLISECONDS,
      warningCategory: this.warningCategory
    });
  }

  /**
   * Check if quote has penny stocks
   *
   * @param: quote
   * @returns: true if quotes have penny stocks
   * TODO: move to the backend
   * returns true if any value in the time series data is below the penny-stock value
   */
  private isPennyStock(quote: DetailedQuote): boolean {
    if (quote === undefined ||
        quote.defaultTimeSeries === undefined ||
        quote.defaultTimeSeries.closeSeries === undefined) {
      return false;
    }

    for (const data of quote.defaultTimeSeries.closeSeries) {
      if (data[1] < this.pennyStockValue) {
        return true;
      }
    }
    return false;
  }
}
